import React, {useEffect, useMemo} from "react";
import {useController, useWatch} from "react-hook-form";
import {FormComponentProps, getDontRegister, StaticFieldComponent} from "../../DynamicFormTypes.ts";
import {useFormMethods, useFormStatic} from "../../DyanmicFormContext.tsx";
import {isEqual} from "lodash";
import {StaticDisplay} from "./StaticDisplay.tsx";
import {determineExternalWatch} from "./staticFieldUtils.ts";
import {useValidationRules} from "../../hooks/useValidationRules.tsx";


const StaticFieldRenderer: React.FC<FormComponentProps<StaticFieldComponent> & { registerWithForm?: boolean }> = ({ component, registerWithForm = true}) => {
    const { formMethods } = useFormMethods()
    const {metaData} = useFormStatic()
    const validate = useValidationRules({ component });

    const shouldIRegister = useMemo(() => {
        if (!registerWithForm) {
            return false;
        }
        const dontRegister = getDontRegister(component);
        if (dontRegister) {
            console.debug("StaticField not registering with form due being a component we dont care about:", component.fieldKey, component);
            return false;
        }

        return true;
    }, [component.fieldKey, component.formatAs, registerWithForm])

    // Determine which field to watch externally
    const externalWatchedField = useMemo(() => {
            return determineExternalWatch(component, shouldIRegister);
        },
        [component.fieldKey, component.watchFieldKey, shouldIRegister]
    );

    const externalWatchedValue = useWatch({
        control: formMethods.control,
        name: externalWatchedField || "dummy_typescript_wants_this_to_not_be_undefined",
        disabled: !externalWatchedField
    });

    const parentController= useController({
            name: component.fieldKey,
            control: formMethods.control,
            rules: validate
        })

    const displayValue = useMemo(() => {
        if (!shouldIRegister) {
            return component.value;
        }
        // Priority order: external watched field > metadata > controller value > static value
        if (externalWatchedValue !== undefined) {
            return externalWatchedValue;
        }

        if (component.metaDataKey) {
            return metaData[component.metaDataKey as keyof typeof metaData];
        }

        return parentController?.field?.value ?? component.value;
    }, [externalWatchedValue, parentController?.field?.value, shouldIRegister, component.value]);

    // 3. Determine the actual value to use
    useEffect(() => {
        if (!shouldIRegister) {
            console.debug("StaticField not registered with form, skipping form value update");
            return;
        }

        // Only update if we have metadata or external watch field and the values differ
        if (!isEqual(parentController?.field?.value, displayValue)) {
            console.debug("Updating form value for StaticField:",
                component.fieldKey, "from", parentController?.field?.value, "to", displayValue);

            parentController?.field?.onChange(displayValue);
        }
    }, [shouldIRegister, displayValue, parentController?.field?.value, component.fieldKey]);

    return <StaticDisplay component={component} value={displayValue} controller={parentController} />;
};

export const StaticField = React.memo(StaticFieldRenderer, (prevProps, nextProps) => {
    return isEqual(prevProps.component, nextProps.component) && prevProps.registerWithForm === nextProps.registerWithForm;
});